import { useEffect, useState, useRef, useCallback } from "react";
import { useViewModel } from "mvvm/View";
import { useDarkMode, useAppSelector } from "redux/hooks";
import { JobViewModel } from "../ViewModels/JobViewModel";
import { ResponseSectionView } from "./ResponseSectionView";

export function ThreadJobEntryView(props: {
    job: JobViewModel;
    tagList: string[];
    tagUpdater: (jobID: string, tags: string[]) => void;
    registerJobRef: (jobID: string, element: HTMLDivElement | null) => void;
    registerQuestionRef: (jobID: string, element: HTMLDivElement | null) => void;
    parentJobAlias?: string;
    onAnimationStateChange?: (isAnimating: boolean) => void;
    onAddQueryToggle?: (query?: string, queryName?: string) => void;
}) {
    const { job, tagList, tagUpdater, registerJobRef, registerQuestionRef, parentJobAlias, onAnimationStateChange, onAddQueryToggle } = props;
    const entryJob = useViewModel(job);
    const { status, statusMessage, outputState } = entryJob;
    const isOutputLoaded = outputState.status === "loaded";
    const loadedOutput = isOutputLoaded ? outputState.output : null;
    const isOutputLoadFailed = outputState.status === "load-failed";
    const outputLoadError = isOutputLoadFailed ? outputState.error : null;
    const darkMode = useDarkMode();
    const pageSize = useAppSelector(state => {
        const pageSizeSetting = state.user.settings?.page_size ?? "200";
        return parseInt(pageSizeSetting, 10);
    });
    const [ellipsis, setEllipsis] = useState("");
    const [isResponseAnimating, setIsResponseAnimating] = useState(false);
    const jobWrapperRef = useRef<HTMLDivElement | null>(null);
    const responseBoxRef = useRef<HTMLDivElement>(null);
    const tableContainerRef = useRef<HTMLDivElement>(null);

    const registerWrapper = useCallback((node: HTMLDivElement | null) => {
        jobWrapperRef.current = node;
        registerJobRef(entryJob.jobID, node);
    }, [entryJob.jobID, registerJobRef]);

    useEffect(() => {
        tagUpdater(entryJob.jobID, entryJob.tags)
    }, [entryJob.jobID, entryJob.tags, tagUpdater]);

    useEffect(() => {
        const interval = setInterval(() => {
            setEllipsis((prev) => (prev.length < 3 ? prev + "." : ""));
        }, 500);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (responseBoxRef.current && status === "RUNNING" && statusMessage) {
            responseBoxRef.current.scrollTo({
                top: responseBoxRef.current.scrollHeight,
                behavior: 'smooth'
            });
        }
    }, [statusMessage, status]);

    return (
        <div
            ref={registerWrapper}
            style={{ marginBottom: "1.5em" }}
        >
            <ResponseSectionView
                status={status}
                statusMessage={statusMessage}
                outputState={outputState}
                loadedOutput={loadedOutput}
                isOutputLoadFailed={isOutputLoadFailed}
                outputLoadError={outputLoadError}
                darkMode={darkMode}
                parentJobAlias={parentJobAlias}
                defaultPageSize={pageSize}
                jobID={entryJob.jobID}
                job={entryJob}
                responseBoxRef={responseBoxRef}
                tableContainerRef={tableContainerRef}
                ellipsis={ellipsis}
                onAnimationStateChange={(isAnimating) => {
                    setIsResponseAnimating(isAnimating);
                    onAnimationStateChange?.(isAnimating);
                }}
                       onAddQueryToggle={onAddQueryToggle}
            />
        </div>
    );
};

